import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { EditRRSchema, getEditRRSchema, GuestsSchema, ResourceOccupancySchema, ResourceSchema } from './edit-rr.schema'
import { Res, ResType } from 'src/app/types/resources.types'
import { ReservedResourceF } from '../../../../types/reservation-detail.types'
import { getNights } from 'src/utils/date'
import { useHotelDate } from 'src/app/hooks/context/context.hook'

export const useEditRRForm = (
  reservedResource: ReservedResourceF, //
  resourceTypes: ResType[],
  resources: Res[],
  onSubmit: (data: EditRRSchema) => Promise<void>,
  isTimeResource?: boolean,
) => {
  const { t } = useTranslation()
  const hotelDate = useHotelDate()
  const schema = getEditRRSchema(t, undefined, isTimeResource)
  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<EditRRSchema>({ resolver: zodResolver(schema) })

  const value = watch()

  const submitHandler: SubmitHandler<EditRRSchema> = useCallback(
    async (data: EditRRSchema) => {
      await onSubmit(data)
    },
    [onSubmit],
  )

  const submitForm = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  useEffect(() => {
    if (JSON.stringify(value.hotelDate) !== JSON.stringify(hotelDate)) {
      setValue('hotelDate', hotelDate, { shouldValidate: isSubmitted })
      if (isSubmitted) trigger()
    }
  }, [hotelDate])

  useEffect(() => {
    if (JSON.stringify(value.availableResourceTypes) !== JSON.stringify(resourceTypes)) {
      setValue('availableResourceTypes', resourceTypes, { shouldValidate: isSubmitted })
      if (isSubmitted) trigger()
    }
  }, [resourceTypes])

  useEffect(() => {
    if (JSON.stringify(value.availableResources) !== JSON.stringify(resources)) {
      setValue('availableResources', resources, { shouldValidate: isSubmitted })
      if (isSubmitted) trigger()
    }
  }, [resources])

  useEffect(() => {
    if (JSON.stringify(value.start) !== JSON.stringify(reservedResource.start)) {
      setValue('start', reservedResource.start)
    }
    if (JSON.stringify(value.end) !== JSON.stringify(reservedResource.end)) {
      setValue('end', reservedResource.end)
    }
    if (JSON.stringify(value.time) !== JSON.stringify(reservedResource.time)) {
      if (reservedResource.time) {
        setValue('time', { from: reservedResource.time.from, to: reservedResource.time.to })
      }
    }
    if (value.rateId !== reservedResource.rate.id) {
      setValue('rateId', reservedResource.rate.id)
    }
    const guestsSchema = getGuestsSchema(reservedResource)
    if (JSON.stringify(value.guests) !== JSON.stringify(guestsSchema)) {
      setValue('guests', guestsSchema)
    }

    let resource: ResourceSchema | undefined = undefined
    const firstResourceOccupancy = reservedResource.resourceOccupancies[0]
    if (firstResourceOccupancy?.reservedResourceType.id) {
      resource = {
        resourceTypeId: firstResourceOccupancy.reservedResourceType.id,
        resourceId: firstResourceOccupancy?.resource?.id ?? null,
      }
    } else {
      resource = {
        resourceTypeId: reservedResource.currentResourceOccupancy.reservedResourceType.id, //
        resourceId: reservedResource.currentResourceOccupancy.resource?.id ?? null,
      }
    }
    if (JSON.stringify(value.resource) !== JSON.stringify(resource)) {
      setValue('resource', resource)
    }

    const price = reservedResource.price

    if (value.price !== price) {
      setValue('price', price)
    }
    const averagePrice = Math.round(price / getNights(reservedResource.start, reservedResource.end))
    if (value.averagePrice !== averagePrice) {
      setValue('averagePrice', averagePrice)
    }

    const resourceOccupancies = getResourceOccupancySchema(reservedResource)
    if (JSON.stringify(value.resourceOccupancies) !== JSON.stringify(resourceOccupancies)) {
      setValue('resourceOccupancies', resourceOccupancies)
    }

    // console.log('useEffect - Valore iniziale totale')
    // console.log(price)
    // console.log('useEffect - getNights(reservedResource.start, reservedResource.end)')
    // console.log(getNights(reservedResource.start, reservedResource.end))

    // console.log('useEffect - Valore iniziale medio')
    // console.log(averagePrice)

    if (value.phase !== reservedResource.phase) setValue('phase', reservedResource.phase)
  }, [reservedResource])

  const triggerSubmitted = useCallback(() => {
    if (isSubmitted) trigger()
  }, [isSubmitted, trigger])

  return { control, errors, submitForm, watch, triggerSubmitted, setValue, isSubmitted, trigger }
}

function getGuestsSchema(reservedResource: ReservedResourceF): GuestsSchema {
  const childOccupancies = reservedResource.childOccupancies ?? []
  const children = (reservedResource.rate.childRule.childDiscount ?? []).map((c) => ({
    childId: c.child.id,
    noOccupancy: c.child.noOccupancy,
    quantity: childOccupancies.find((cc) => cc.child.id === c.child.id)?.quantity ?? null,
  }))

  return {
    adults: reservedResource.adults,
    children,
  }
}

function getResourceOccupancySchema(reservedResource: ReservedResourceF): ResourceOccupancySchema[] {
  const result: ResourceOccupancySchema[] = reservedResource.resourceOccupancies.map<ResourceOccupancySchema>((ro) => ({
    start: ro.start,
    end: ro.end,
    phase: ro.phase,
    reservedResourceTypeId: ro.reservedResourceType.id,
    assignedResourceTypeId: ro.assignedResourceType.id,
    resourceId: ro.resource?.id,
  }))
  return result
}
